import { partition } from 'es-toolkit';
import React, { ReactNode } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, Paper } from '@mui/material';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';

import { getColorHexStringFromCapacityRatio } from '../../../capacityColors';
import { loadCaseOrCheckResultOverCapacity } from '../../../library/loadCaseOrCheckResultOverCapacity';
import { SelectableItemEntry } from '../../../library/selection/types';
import { RootState } from '../../../store';
import { loadActiveAnalysisResults } from '../../../store/actions/loadAnalysisResults';
import { selectLoadCase } from '../../../store/actions/selectActions';
import { displaySelectedCheckResultLoadCase } from '../../../store/actions/visualOptionActions';
import {
  getLoadCasesForActiveComponentDesignSorted,
  LoadCaseWithCapacity,
} from '../../../store/selectors/getLoadCasesForActiveComponentDesignSortedSelector';
import { getSingleSelectedItemEntryOrNull } from '../../../store/selectors/getSelectedItemSelector';
import {
  loadCasePanelExpandedItemIdsSelect,
  loadCasePanelShowSet,
  loadCasePanelSortType,
} from '../../../store/slices/panelDataSlice';

interface Item {
  readonly itemId: string;
  readonly label: ReactNode;
  readonly children?: readonly Item[];
}

interface LoadCaseItem extends Item {
  readonly type: 'loadCase';
  readonly loadCase: LoadCaseWithCapacity;
  readonly loadCaseGuid: string;
}

function isLoadCaseItem(item: Item): item is LoadCaseItem {
  return (item as LoadCaseItem).type === 'loadCase';
}

const getColorFromCapacityOrUndefined = (capacity: number | undefined) => {
  if (capacity == null) return 'white';
  return getColorHexStringFromCapacityRatio(capacity);
};

const isCapacityUndefined = (loadCaseDataItem: LoadCaseItem) => loadCaseDataItem.loadCase.capacity === undefined;

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;

type Props = StateProps & DispatchProps;

class LoadCasesPanel extends React.Component<Props> {
  findItem(items: readonly Item[], itemId: string | null): Item | null {
    for (const item of items) {
      if (item.itemId === itemId) return item;
      if (item.children) {
        const foundItem = this.findItem(item.children, itemId);
        if (foundItem) return foundItem;
      }
    }
    return null;
  }

  onSelectedItemsChange = (items: readonly Item[], itemId: string | null) => {
    const item = this.findItem(items, itemId)!;
    if (itemId === 'PassingLoadCases' || itemId === 'OverCapacityLoadCases') return;
    if (itemId === 'All') {
      this.props.selectLoadCase(null);
      this.props.displaySelectedCheckResultLoadCase(null);
      return;
    }
    if (isLoadCaseItem(item)) {
      this.props.selectLoadCase(item.loadCase.guid);
      this.props.loadActiveAnalysisResults();
      if (this.props.showCheckResultsLoadCases) {
        const selectedLoadCase = this.props.loadCases.find((loadCase) => loadCase.guid === item.loadCase.guid);
        this.props.displaySelectedCheckResultLoadCase(selectedLoadCase!);
      }
    }
  };

  onExpandedItemsChange = (_: React.SyntheticEvent | null, itemIds: string[]) => {
    this.props.setLoadCaseExpandedItemIds(itemIds);
  };

  onSortCheckResults = (option: string, selectedItemEntry?: SelectableItemEntry | null) => {
    this.props.setLoadCaseSortType({ sortType: option, selectedItemEntry: selectedItemEntry ?? null });
  };

  createLoadCaseItems(loadCases: ReadonlyArray<LoadCaseWithCapacity>): LoadCaseItem[] {
    return loadCases.map((loadCase) => {
      const itemId = `loadCase${loadCase.guid}`;

      const manualLoadsIndicator = loadCase.loads.some((load) => load.isManualLoad) ? '*' : '';

      return {
        itemId,
        label: (
          <Box
            component="span"
            sx={{ color: getColorFromCapacityOrUndefined(loadCase.capacity) }}
          >{`${loadCase.index}.${manualLoadsIndicator} ${loadCase.name}`}</Box>
        ),
        type: 'loadCase',
        loadCase,
        loadCaseGuid: loadCase.guid,
      };
    });
  }

  renderSortDropDown() {
    const getDropdownLabel = (sortType: string, entry: SelectableItemEntry | null) => {
      let selectedItemLabel;
      if (entry?.type === 'platePair') {
        selectedItemLabel = 'Plate Pair';
      } else if (entry?.type === 'analogJoint') {
        selectedItemLabel = 'Joint';
      } else if (entry?.type === 'member') {
        selectedItemLabel = 'Member';
      } else if (entry?.type === 'analogLink') {
        selectedItemLabel = 'Link';
      } else {
        throw new Error();
      }

      const sortLabel = sortType === 'csi' ? 'CSI' : 'JSI';
      return `${sortLabel} for ${selectedItemLabel} ${entry.item.name}`;
    };

    const dropdownItems = [
      <MenuItem
        key="sortByWorstCapacity"
        onMouseDown={() => this.onSortCheckResults('capacity')}
        id="sortByWorstCapacity"
        value="capacity"
      >
        Worst Capacity
      </MenuItem>,
      <MenuItem key="sortByIndex" onMouseDown={() => this.onSortCheckResults('index')} id="sortByIndex" value="index">
        Index
      </MenuItem>,
    ];
    const addDropdownItem = (sortType: string) => {
      dropdownItems.push(
        <MenuItem
          key={`sortBy${sortType.charAt(0).toUpperCase() + sortType.slice(1)}`}
          onMouseDown={() => this.onSortCheckResults(sortType, this.props.selectedItemEntry)}
          value={sortType}
        >
          {getDropdownLabel(sortType, this.props.selectedItemEntry)}
        </MenuItem>,
      );
    };
    if (this.props.loadCases.find((loadCase) => loadCase.csi !== undefined) !== undefined) {
      addDropdownItem('csi');
    }
    if (this.props.loadCases.find((loadCase) => loadCase.jsi !== undefined) !== undefined) {
      addDropdownItem('jsi');
    }

    return (
      <FormControl sx={{ m: 1, marginRight: 'auto', minWidth: 120 }}>
        <Select
          variant="standard"
          value={this.props.sortType}
          inputProps={{
            name: 'sort',
            id: 'dropDown',
          }}
        >
          {dropdownItems}
        </Select>
      </FormControl>
    );
  }

  renderItems(items: readonly Item[] | undefined) {
    return (
      items?.map((item) => (
        <TreeItem key={item.itemId} itemId={item.itemId} label={item.label}>
          {this.renderItems(item.children)}
        </TreeItem>
      )) ?? null
    );
  }

  render() {
    const selectedItemId = this.props.selectedLoadCaseGuid ? `loadCase${this.props.selectedLoadCaseGuid}` : 'All';
    const loadCaseItems = this.createLoadCaseItems(this.props.loadCases);

    const items: Item[] = [];

    if (loadCaseItems.length) {
      items.push({ itemId: 'All', label: 'Results for All Load Cases' });
    }

    // Group LoadCases by under/over capacity; sort them by closest to over capacity descending
    if (this.props.sortType === 'capacity' && !loadCaseItems.some(isCapacityUndefined)) {
      const partitionedLoadCaseItems = partition(
        loadCaseItems,
        (loadCaseItem) => !loadCaseOrCheckResultOverCapacity(loadCaseItem.loadCase.capacity!),
      );

      if (partitionedLoadCaseItems[1].length !== 0) {
        const [, overCapacityLoadCaseItems] = partitionedLoadCaseItems;
        const overCapacityLoadCasesItem = {
          itemId: 'OverCapacityLoadCases',
          label: `Over-Capacity Load Cases (${overCapacityLoadCaseItems.length})`,
          children: overCapacityLoadCaseItems,
        };

        items.push(overCapacityLoadCasesItem);
      }

      if (partitionedLoadCaseItems[0].length !== 0) {
        const [passingLoadCaseItems] = partitionedLoadCaseItems;
        const passingLoadCasesItem = {
          itemId: 'PassingLoadCases',
          label: `Passing Load Cases (${passingLoadCaseItems.length})`,
          children: passingLoadCaseItems,
        };

        items.push(passingLoadCasesItem);
      }
    } else {
      const allLoadCasesItem = {
        itemId: 'AllLoadCases',
        label: `Load Cases (${loadCaseItems.length})`,
        children: loadCaseItems,
      };

      items.push(allLoadCasesItem);
    }

    return (
      <Paper variant="outlined" elevation={0} id="loadCasePanel">
        <Box component="form" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end' }} autoComplete="off">
          {this.renderSortDropDown()}
        </Box>
        <Divider />
        <SimpleTreeView
          sx={{ p: 1 }}
          expandedItems={
            this.props
              .expandedItemIds as string[] /* FIXME Submit a PR to MUI to make the prop accept a readonly array */
          }
          selectedItems={selectedItemId}
          onExpandedItemsChange={this.onExpandedItemsChange}
          onSelectedItemsChange={(_, itemId) => {
            this.onSelectedItemsChange(items, itemId);
          }}
        >
          {this.renderItems(items)}
        </SimpleTreeView>
      </Paper>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  showCheckResultsLoadCases: state.visualOptions.showCheckResultsLoadCases,
  expandedItemIds: state.panelData.loadCaseExpandedItemIds,
  open: state.panelData.loadCasePanelOpen,
  activeComponentDesignGuid: state.selections.activeComponentDesignGuid,
  loadCases: getLoadCasesForActiveComponentDesignSorted(state),
  selectedLoadCaseGuid: state.selections.selectedCheckResultsLoadCaseGuid,
  sortType: state.panelData.loadCaseSortType,
  selectedItemEntry: getSingleSelectedItemEntryOrNull(state),
  sortSelectedItemEntry: state.panelData.loadCaseSortSelectedItemEntry,
});

const actionCreators = {
  selectLoadCase,
  displaySelectedCheckResultLoadCase,
  setShowLoadCasePanel: loadCasePanelShowSet,
  setLoadCaseExpandedItemIds: loadCasePanelExpandedItemIdsSelect,
  setLoadCaseSortType: loadCasePanelSortType,
  loadActiveAnalysisResults,
};

export default connect(mapStateToProps, actionCreators)(LoadCasesPanel);
